import { Popconfirm } from '@/components/Popconfirm';
import { Text } from '@/components/ui';
import { SquarePenIcon, TrashIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Ticket } from '@/lib/api/tickets';
import { parseLocalDate } from '@/lib/utils/parse-local-date';

interface TicketCardProps {
  ticket: Ticket;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function TicketCard({ ticket, onEdit, onDelete }: TicketCardProps) {
  const { t } = useTranslation();

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
  };

  return (
    <div className="p-md border-transparent-dark-16 rounded-2xl border shadow-xs">
      <header className="flex items-start justify-between">
        <div className="mr-2 min-w-0 flex-1">
          <Text size="lg" strong className="block truncate">
            {ticket.title}
          </Text>
          <Text className="text-neutral-muted">{ticket.number}</Text>
        </div>
        <div className="gap-2xs flex items-center">
          {onDelete && (
            <Popconfirm
              onConfirm={onDelete}
              title={t('tickets.card.deleteAction.title')}
              description={t('tickets.card.deleteAction.description')}
              variant="danger"
              confirmLabel={t('tickets.card.deleteAction.label')}
            >
              <button
                type="button"
                className="text-neutral-muted hover:text-error-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TrashIcon size={16} />
              </button>
            </Popconfirm>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="text-neutral-muted hover:text-neutral-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SquarePenIcon size={16} />
            </button>
          )}
        </div>
      </header>

      <div className="mt-md gap-4xs flex flex-col">
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('tickets.card.date')}</Text>
          <Text strong>{format(parseLocalDate(ticket.date), 'dd/MM/yyyy')}</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('tickets.card.workedTime')}</Text>
          <Text strong>{formatTime(ticket.workedTime)}</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('tickets.card.debitedTime')}</Text>
          <Text strong>{formatTime(ticket.debitedTime)}</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('tickets.card.user')}</Text>
          <Text strong>{ticket.user.name}</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('tickets.card.contract')}</Text>
          <Text strong>{ticket.contract.company.name}</Text>
        </div>
      </div>
    </div>
  );
}
